<template>
  <div v-if="business">
    <el-form :model="business" :rules="rules" ref="form">
      <!-- Topbar -->
      <TopBarTitleComponent title="Directorio" :showBackIcon="true">
        <template slot="right-area">
          <div
            class="d-flex justify-content-center justify-content-md-end align-items-center"
          >
            <div class="buttons dir-edit-buttons">
              <template v-if="isDisabled">
                <ButtonDeleteComponent
                  @onConfirmDelete="onConfirmDelete"
                  class="mr-1"
                />
                <button
                  type="button"
                  class="topbar-action-btn"
                  @click="handleToggleEdit"
                  title="Editar"
                >
                  <i class="el-icon-edit"></i>
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="btn md__btn-third-secondary mr-2 dir-btn-cancel"
                  @click="handleCancelEdit"
                >
                  Cancelar
                </button>
                <ButtonPrimaryComponent
                  title="Guardar"
                  @onSaveClick="handleSaveClick"
                />
              </template>
            </div>
          </div>
        </template>
      </TopBarTitleComponent>

      <div class="dir-content-body">
        <!-- Breadcrumb -->
        <div class="dir-breadcrumb">
          <a @click="$router.push({ name: 'business.main' })">Directorio</a>
          <span class="dir-breadcrumb-sep">›</span>
          <span class="dir-breadcrumb-current">{{
            business.name || "Empresa"
          }}</span>
        </div>

        <div class="dir-detail-layout">
          <!-- Left: Main content -->
          <div class="dir-detail-main">
            <!-- Hero -->
            <CompanyHero
              :business="business"
              :contactsCount="business.contacts ? business.contacts.length : 0"
              :quotesCount="quotesCount"
              @upload-logo="triggerLogoUpload"
            />

            <!-- Internal tabs -->
            <div class="dir-internal-tabs">
              <button
                type="button"
                class="dir-tab"
                :class="{ active: internalTab === 'info' }"
                @click="internalTab = 'info'"
              >
                Información
              </button>
              <button
                type="button"
                class="dir-tab"
                :class="{ active: internalTab === 'documents' }"
                @click="internalTab = 'documents'"
              >
                Documentos
              </button>
              <button
                type="button"
                class="dir-tab d-lg-none"
                :class="{ active: internalTab === 'contacts' }"
                @click="internalTab = 'contacts'"
              >
                Contactos ({{
                  business.contacts ? business.contacts.length : 0
                }})
              </button>
            </div>

            <!-- Tab: Información (READ MODE) -->
            <div v-if="internalTab === 'info' && isDisabled">
              <div class="dir-info-section">
                <h5 class="dir-info-section-title">Información básica</h5>
                <div class="dir-info-grid">
                  <div class="dir-info-item">
                    <span class="dir-info-label">Tipo empresa</span>
                    <span class="dir-info-value" v-if="business.contact_type">
                      {{
                        business.contact_type === 1 ? "Cliente" : "Proveedor"
                      }}
                    </span>
                    <span class="dir-info-value-empty" v-else
                      >No especificado</span
                    >
                  </div>
                  <div class="dir-info-item">
                    <span class="dir-info-label">Nombre comercial</span>
                    <span class="dir-info-value" v-if="business.tradename">{{
                      business.tradename
                    }}</span>
                    <span class="dir-info-value-empty" v-else
                      >No especificado</span
                    >
                  </div>
                  <div class="dir-info-item">
                    <span class="dir-info-label">Correo</span>
                    <a
                      v-if="business.email"
                      class="dir-info-value-link"
                      :href="'mailto:' + business.email"
                      >{{ business.email }}</a
                    >
                    <span class="dir-info-value-empty" v-else
                      >No especificado</span
                    >
                  </div>
                  <div class="dir-info-item">
                    <span class="dir-info-label">Teléfono</span>
                    <span class="dir-info-value" v-if="business.phone">{{
                      business.phone
                    }}</span>
                    <span class="dir-info-value-empty" v-else
                      >No especificado</span
                    >
                  </div>
                  <div class="dir-info-item">
                    <span class="dir-info-label">Sitio web</span>
                    <a
                      v-if="business.web"
                      class="dir-info-value-link"
                      :href="business.web"
                      target="_blank"
                      >{{ business.web }}</a
                    >
                    <span class="dir-info-value-empty" v-else
                      >No especificado</span
                    >
                  </div>
                  <div class="dir-info-item">
                    <span class="dir-info-label">Ciudad</span>
                    <span class="dir-info-value" v-if="business.city">{{
                      business.city.name
                    }}</span>
                    <span class="dir-info-value-empty" v-else
                      >No especificado</span
                    >
                  </div>
                </div>
              </div>

              <div class="dir-info-section">
                <h5 class="dir-info-section-title">
                  Información de facturación
                </h5>
                <div class="dir-info-grid">
                  <div class="dir-info-item">
                    <span class="dir-info-label">Tipo documento</span>
                    <span
                      class="dir-info-value"
                      v-if="business.document_type"
                      >{{ getDocumentTypeName(business.document_type) }}</span
                    >
                    <span class="dir-info-value-empty" v-else
                      >No especificado</span
                    >
                  </div>
                  <div class="dir-info-item">
                    <span class="dir-info-label">Número</span>
                    <span class="dir-info-value" v-if="business.document">{{
                      business.document
                    }}</span>
                    <span class="dir-info-value-empty" v-else
                      >No especificado</span
                    >
                  </div>
                  <div class="dir-info-item">
                    <span class="dir-info-label">Razón social</span>
                    <span class="dir-info-value" v-if="business.name">{{
                      business.name
                    }}</span>
                    <span class="dir-info-value-empty" v-else
                      >No especificado</span
                    >
                  </div>
                  <div class="dir-info-item">
                    <span class="dir-info-label">Dirección</span>
                    <span class="dir-info-value" v-if="business.address">{{
                      business.address
                    }}</span>
                    <span class="dir-info-value-empty" v-else
                      >No especificado</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Tab: Información (EDIT MODE) -->
            <div v-if="internalTab === 'info' && !isDisabled">
              <div class="dir-info-section">
                <h5 class="dir-info-section-title">Información básica</h5>
                <b-row>
                  <b-col xs="12" sm="12" md="6">
                    <el-form-item prop="contact_type">
                      <label class="font-semi-bold m-0">Tipo de empresa</label>
                      <el-select
                        v-model="business.contact_type"
                        placeholder="Seleccionar"
                      >
                        <el-option
                          v-for="(type, i) in businessTypes"
                          :key="i"
                          :label="type.name"
                          :value="type.id"
                        />
                      </el-select>
                    </el-form-item>
                  </b-col>
                  <b-col xs="12" sm="12" md="6">
                    <el-form-item prop="tradename">
                      <label class="font-semi-bold m-0">Nombre Comercial</label>
                      <el-input type="text" v-model="business.tradename" />
                    </el-form-item>
                  </b-col>
                  <b-col xs="12" sm="12" md="6">
                    <el-form-item prop="email">
                      <label class="font-semi-bold m-0">Correo</label>
                      <el-input type="text" v-model="business.email" />
                    </el-form-item>
                  </b-col>
                  <b-col xs="12" sm="12" md="6">
                    <el-form-item prop="phone">
                      <label class="font-semi-bold m-0">Teléfono</label>
                      <el-input type="text" v-model="business.phone" />
                    </el-form-item>
                  </b-col>
                  <b-col xs="12" sm="12" md="6">
                    <el-form-item prop="web">
                      <label class="font-semi-bold m-0">Sitio web</label>
                      <el-input type="text" v-model="business.web" />
                    </el-form-item>
                  </b-col>
                  <b-col xs="12" sm="12" md="6">
                    <el-form-item prop="city_id">
                      <label class="font-semi-bold m-0">Ciudad</label>
                      <el-select
                        filterable
                        v-model="business.city_id"
                        placeholder="Seleccionar"
                      >
                        <el-option
                          v-for="city in cities"
                          :key="city.id"
                          :label="city.name"
                          :value="city.id"
                        />
                      </el-select>
                    </el-form-item>
                  </b-col>
                </b-row>
              </div>

              <div class="dir-info-section">
                <h5 class="dir-info-section-title">
                  Información de facturación
                </h5>
                <b-row>
                  <b-col xs="12" sm="12" md="6">
                    <el-form-item prop="document_type">
                      <label class="font-semi-bold m-0"
                        >Tipo de documento</label
                      >
                      <el-select
                        v-model="business.document_type"
                        placeholder="Seleccionar"
                        @change="loadDefaultValue()"
                      >
                        <el-option
                          v-for="doc in documentTypes"
                          :key="doc.id"
                          :label="doc.name"
                          :value="doc.id"
                        />
                      </el-select>
                    </el-form-item>
                  </b-col>
                  <b-col xs="12" sm="12" md="6">
                    <el-form-item prop="document">
                      <label class="font-semi-bold m-0">Documento</label>
                      <el-input type="text" v-model="business.document" />
                    </el-form-item>
                  </b-col>
                  <b-col xs="12" sm="12" md="6">
                    <el-form-item prop="name">
                      <label class="font-semi-bold m-0">Razón Social</label>
                      <el-input type="text" v-model="business.name" />
                    </el-form-item>
                  </b-col>
                  <b-col xs="12" sm="12" md="6">
                    <el-form-item prop="address">
                      <label class="font-semi-bold m-0">Dirección</label>
                      <el-input type="text" v-model="business.address" />
                    </el-form-item>
                  </b-col>
                </b-row>
              </div>

              <!-- Logo upload (hidden) -->
              <FileUploadComponent
                ref="logoUpload"
                title="Subir Logo"
                :showFileList="false"
                :preview="business.image"
                @fileBase64="onFileBase64"
                style="display: none;"
              />
            </div>

            <!-- Tab: Documentos -->
            <div v-if="internalTab === 'documents'">
              <DocumentsTab :businessId="business.id" />
            </div>

            <!-- Tab: Contactos (mobile only) -->
            <div v-if="internalTab === 'contacts'" class="d-lg-none">
              <ContactsPanel
                :contacts="business.contacts || []"
                @add-contact="addContacts"
                @select-contact="onSelectContact"
                @view-all="$router.push({ name: 'business.main' })"
              />
            </div>
          </div>

          <!-- Right: Contacts Panel (desktop only) -->
          <div class="dir-detail-right d-none d-lg-block">
            <ContactsPanel
              :contacts="business.contacts || []"
              @add-contact="addContacts"
              @select-contact="onSelectContact"
              @view-all="$router.push({ name: 'business.main' })"
            />
          </div>
        </div>
      </div>
    </el-form>

    <!-- Contact modal (fullscreen) -->
    <div
      class="modal-overlay"
      v-if="dialogEditContact"
      @click.self="closeContactModal"
    >
      <div class="modal-box">
        <button class="modal-close" @click="closeContactModal">
          <XIcon :size="16" />
        </button>
        <div class="modal-body">
          <h2 class="modal-title">
            {{
              selectedContact && selectedContact.id
                ? "Editar contacto"
                : "Crear contacto"
            }}
          </h2>
          <p class="modal-sub">
            {{
              selectedContact && selectedContact.id
                ? "Modifica los datos del contacto"
                : "Agrega un contacto a esta empresa"
            }}
          </p>

          <div
            class="contact-photo-upload"
            @click="$refs.contactPhotoInput && $refs.contactPhotoInput.click()"
          >
            <UserIcon :size="28" color="#9b9bab" />
            <span>Subir Foto</span>
          </div>
          <input
            type="file"
            ref="contactPhotoInput"
            accept="image/*"
            style="display:none"
            @change="handleContactPhotoChange"
          />

          <div class="modal-section-title">Informaci&oacute;n de Contacto</div>
          <div class="modal-form-grid">
            <div class="modal-form-field">
              <label>Nombre de contacto</label>
              <input v-model="contactForm.name" placeholder="Nombre completo" />
            </div>
            <div class="modal-form-field">
              <label>Correo</label>
              <input
                v-model="contactForm.email"
                placeholder="correo@empresa.com"
              />
            </div>
            <div class="modal-form-field">
              <label>Tel&eacute;fono</label>
              <input
                v-model="contactForm.phone"
                placeholder="+593 99 000 0000"
              />
            </div>
            <div class="modal-form-field">
              <label>Cargo</label>
              <input
                v-model="contactForm.position"
                placeholder="Gerente, Contador..."
              />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn md__btn-primary" @click="saveContactFromModal">
              {{
                selectedContact && selectedContact.id
                  ? "Guardar cambios"
                  : "Guardar contacto"
              }}
            </button>
            <button
              class="btn md__btn-third-secondary"
              @click="closeContactModal"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TopBarTitleComponent from "../../components/TopBarTitle";
import FileUploadComponent from "../../components/FileUpload";
import ButtonPrimaryComponent from "../../components/ButtonPrimary";
import ButtonDeleteComponent from "../../components/ButtonDelete";
import CompanyHero from "./CompanyHero";
import ContactsPanel from "./ContactsPanel";
import DocumentsTab from "./DocumentsTab";
import { XIcon, UserIcon } from "@/components/Icons/LucideIcons";

import businessService from "../services/businessService";
import CitiesServices from "@/services/CitiesServices";
import Validate from "@/assets/validate";
import {
  documentTypes,
  businessTypes,
  validateDocumentByCountry
} from "@/services/Utils";

export default {
  name: "BusinessEditComponent",
  props: {
    businessIdProp: { type: [Number, String], default: null },
    isEmbedded: { type: Boolean, default: false }
  },
  data() {
    const checkDocument = (rule, value, callback) => {
      if (!value) return callback();
      const { document_type } = this.business;
      switch (document_type) {
        case 1:
          if (value.length !== 13)
            return callback(new Error("RUC debe tener 13 dígitos"));
          break;
        case 2:
          if (value.length !== 10)
            return callback(new Error("Cédula debe tener 10 dígitos"));
          break;
        case 4:
          if (value.length !== 13 && value != 9999999999999)
            return callback(new Error("Consumidor Final: 9999999999999"));
          break;
        // Tipo 5 (Identificación del Exterior): sin restricción de longitud
        default:
          break;
      }
      // Validación algorítmica Ecuador si el negocio tiene country_code = EC
      const countryCode = this.currentCountryCode;
      const ecError = validateDocumentByCountry(
        document_type,
        value,
        countryCode
      );
      if (ecError) return callback(new Error(ecError));
      callback();
    };
    return {
      documentTypes,
      businessTypes,
      business: null,
      isDisabled: true,
      internalTab: "info",
      cities: [],
      quotesCount: 0,
      dialogEditContact: false,
      selectedContact: null,
      contactForm: { name: "", email: "", phone: "", position: "", image: "" },
      rules: {
        tradename: [Validate.rules.required()],
        email: [Validate.rules.required(), Validate.rules.email()],
        document: [{ validator: checkDocument, trigger: "blur" }],
        contact_type: [Validate.rules.required("change")]
      }
    };
  },
  computed: {
    currentCountryCode() {
      const user = this.$store.getters.getUser;
      return user && user.user_business
        ? user.user_business.country_code || "EC"
        : "EC";
    },
    resolvedBusinessId() {
      return (
        this.businessIdProp ||
        (this.$route.params && this.$route.params.id) ||
        null
      );
    },
    getSyncRules() {
      return Validate;
    },
    profileCompleteness() {
      if (!this.business) return 0;
      const fields = [
        "name",
        "tradename",
        "email",
        "phone",
        "address",
        "document",
        "document_type",
        "contact_type",
        "city_id",
        "web",
        "image"
      ];
      const filled = fields.filter(f => this.business[f]).length;
      return Math.round((filled / fields.length) * 100);
    }
  },
  methods: {
    loadDefaultValue() {
      this.business.document =
        this.business.document_type == 4 ? "9999999999999" : "";
    },
    handleToggleEdit() {
      this.isDisabled = false;
    },
    handleCancelEdit() {
      this.isDisabled = true;
      this.handleGetBusinessId(this.resolvedBusinessId);
    },
    triggerLogoUpload() {
      if (!this.isDisabled && this.$refs.logoUpload) {
        this.$refs.logoUpload.$el.querySelector("input[type=file]")?.click();
      }
    },
    onFileBase64(base64) {
      this.business.image = base64;
    },
    getDocumentTypeName(type) {
      const doc = this.documentTypes.find(d => d.id === type);
      return doc ? doc.name : "—";
    },
    handleSaveClick() {
      // Un RUC/cédula pegado desde Excel/PDF/portal del SRI suele traer un
      // espacio invisible al inicio o al final — sin este trim, "value.length"
      // contaba ese espacio y fallaba "debe tener 13 dígitos" aunque el
      // usuario viera 13 dígitos correctos. Se recorta ANTES de validar para
      // que valide y guarde el mismo valor limpio que el usuario ve.
      if (this.business.document) {
        this.business.document = this.business.document.trim();
      }
      this.$refs.form.validate(async valid => {
        if (valid) {
          this.$store.dispatch("toggleRequestLoading");
          try {
            let response = await businessService
              .updateBusinessId(this.business)
              .finally(() => this.$store.dispatch("toggleRequestLoading"));
            const { success, message } = response.data;
            if (success) {
              this.business = response.data.client;
              this.isDisabled = true;
              this.$notifications.success({ message });
              if (this.isEmbedded) {
                this.$emit("onSaved", { client: response.data.client });
              }
            }
          } catch (error) {
            if (error.response)
              this.$notifications.error({
                message: error.response.data.message
              });
          }
        }
      });
    },
    addContacts() {
      this.selectedContact = null;
      this.contactForm = {
        name: "",
        email: "",
        phone: "",
        position: "",
        image: "",
        client_id: this.business.id
      };
      this.dialogEditContact = true;
    },
    onSelectContact(contact) {
      this.selectedContact = contact;
      this.contactForm = { ...contact };
      this.dialogEditContact = true;
    },
    closeContactModal() {
      this.dialogEditContact = false;
      this.selectedContact = null;
    },
    handleContactPhotoChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = e => {
        this.contactForm.image = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    async saveContactFromModal() {
      if (!this.contactForm.name || !this.contactForm.email) {
        this.$notifications.error({
          message: "Nombre y correo son requeridos"
        });
        return;
      }
      this.$store.dispatch("toggleRequestLoading");
      try {
        const payload = {
          ...this.contactForm,
          client_id: this.business.id
        };
        let response;
        if (this.selectedContact && this.selectedContact.id) {
          response = await businessService.updateContactId(payload);
        } else {
          response = await businessService.addContact(payload);
        }
        if (response && response.data && response.data.success) {
          this.$notifications.success({ message: response.data.message });
          this.closeContactModal();
          this.handleGetBusinessId(this.resolvedBusinessId);
        } else if (response && response.data && response.data.message) {
          this.$notifications.error({ message: response.data.message });
        }
      } catch (error) {
        const msg =
          error.response && error.response.data
            ? error.response.data.message
            : "Error al guardar contacto";
        this.$notifications.error({ message: msg });
      } finally {
        this.$store.dispatch("toggleRequestLoading");
      }
    },
    _handleEsc(e) {
      if (e.key === "Escape") this.closeContactModal();
    },
    onSuccessContactEdit() {
      this.closeContactModal();
      this.handleGetBusinessId(this.resolvedBusinessId);
    },
    async handleGetBusinessId(businessId) {
      this.$store.dispatch("toggleRequestLoading");
      try {
        let response = await businessService
          .getBusinessId(businessId)
          .finally(() => this.$store.dispatch("toggleRequestLoading"));
        if (response.data.client) {
          this.business = response.data.client;
          this.quotesCount = this.business.quotes
            ? this.business.quotes.length
            : 0;
        }
      } catch (error) {
        return false;
      }
    },
    async getCities() {
      try {
        let response = await CitiesServices.getCities();
        if (response.data.success) this.cities = response.data.cities;
      } catch (e) {
        return false;
      }
    },
    async onConfirmDelete() {
      this.$store.dispatch("toggleRequestLoading");
      try {
        let response = await businessService
          .deleteBusinessId(this.business.id)
          .finally(() => this.$store.dispatch("toggleRequestLoading"));
        if (response.data.success) {
          this.$router.push({ name: "business.main" });
        }
      } catch (error) {
        return false;
      }
    }
  },
  components: {
    TopBarTitleComponent,
    FileUploadComponent,
    ButtonPrimaryComponent,
    ButtonDeleteComponent,
    CompanyHero,
    ContactsPanel,
    DocumentsTab,
    XIcon,
    UserIcon
  },
  mounted() {
    document.addEventListener("keydown", this._handleEsc);
    this.getCities();
    if (this.resolvedBusinessId)
      this.handleGetBusinessId(this.resolvedBusinessId);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this._handleEsc);
  }
};
</script>

<style lang="scss" scoped>
.topbar-action-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #31355c;
  transition: all 0.15s;
  margin-right: 4px;

  &:hover {
    background: #f0f2f7;
  }
}
.el-form-item {
  margin-bottom: 8px !important;
}

.dir-edit-buttons {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0;
}

@media (max-width: 480px) {
  .dir-btn-cancel {
    padding: 6px 10px;
    font-size: 12px;
  }
}
</style>
